/** Thin API client for the TEBIKI Worker. Base URL from VITE_API_URL (dev: wrangler dev).
 *
 * The backend is implemented in parallel; every call site must tolerate a missing/erroring
 * API. `api()` throws on non-2xx so callers can surface an honest loading / empty / error /
 * offline state (NO demo fallback). Helpers below return typed promises that map onto the
 * @tebiki/shared contract.
 */
import type { Manual, JobProgress, ManualStatus, Step, BBox, StepHighlights, ManualChecklist, ChecklistProgress, Membership, Store, Division, User, Book, BookAudience, ReadingProgressEntry, ReadingProgressSummaryRow } from "@tebiki/shared";

/**
 * API origin resolution — no per-visit config needed on the deployed site.
 *  1. `VITE_API_URL` (build-time override) wins when set — e.g. a cross-origin Worker URL.
 *  2. Local dev (localhost / 127.0.0.1 / *.local): the wrangler dev Worker on :8787.
 *  3. Deployed: same-origin ("") so calls go to `/api/...` on the current host. The Pages
 *     site proxies `/api/*` to the API Worker (see apps/web/functions/api/[[path]].ts), so
 *     there is no cross-origin / mixed-content / "localhost" pitfall in production.
 */
function resolveApiBase(): string {
  const env = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (env) return env.replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".local");
    return isLocal ? "http://localhost:8787" : "";
  }
  return "http://localhost:8787";
}

const BASE = resolveApiBase();

/** True when the browser reports no connectivity (used to show an honest offline state). */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

let token: string | null = localStorage.getItem("tebiki_token");
export function setToken(t: string | null) {
  token = t;
  if (t) localStorage.setItem("tebiki_token", t);
  else localStorage.removeItem("tebiki_token");
}
export function getToken(): string | null {
  return token;
}
/** True when a session token is present (used to gate nav/logout affordances). */
export function isAuthed(): boolean {
  return !!token;
}
/** Manual logout: drop the session and return to the login screen. */
export function logout(): void {
  setToken(null);
  if (typeof window !== "undefined") window.location.assign("/auth");
}

export type Role = "owner" | "admin" | "editor" | "reviewer" | "viewer";

/** Decoded JWT claims (UI routing only — NOT authoritative; the API enforces org + RBAC). */
export interface Claims {
  sub: string;
  org: string;
  roles: Role[];
  stores: string[];
  exp?: number;
}

/**
 * Decode (NOT verify) the JWT payload for client-side UI routing / nav gating only. Returns null
 * when there is no token, it can't be parsed, or it has expired. The server remains the authority
 * (tenant isolation via WHERE org_id + role checks), so a tampered token only yields 403s.
 */
export function getClaims(): Claims | null {
  const t = token;
  if (!t) return null;
  const seg = t.split(".")[1];
  if (!seg) return null;
  try {
    const json = atob(seg.replace(/-/g, "+").replace(/_/g, "/"));
    const c = JSON.parse(json) as Claims;
    if (typeof c.exp === "number" && c.exp * 1000 < Date.now()) return null; // expired
    return c;
  } catch {
    return null;
  }
}

/** True when the current session holds at least one of the given roles (UI gating only). */
export function hasRole(...allowed: Role[]): boolean {
  const roles = getClaims()?.roles ?? [];
  return roles.some((r) => allowed.includes(r));
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) {
    headers.set("content-type", headers.get("content-type") ?? "application/json");
  }
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    // Expired/invalid session: a 401 on an authenticated call means the JWT is dead. Drop it and
    // send the user to re-login instead of letting every screen show a stale/frozen state
    // (which looked like "API未接続" + frozen progress). Skip for the /auth endpoints themselves
    // (a bad PIN there is a normal 401, not an expired session).
    if (res.status === 401 && token && !path.startsWith("/api/auth/")) {
      setToken(null);
      // Background uploads (/api/uploads/*, e.g. the offline-queue auto-flush) drop the dead token
      // but must NOT redirect — otherwise a background flush would yank the user off whatever page
      // they're on (e.g. the camera viewfinder), which looked like "勝手に前の画面に戻る".
      const background = path.startsWith("/api/uploads/");
      if (!background && typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
        window.location.assign("/auth?expired=1");
      }
    }
    throw new ApiError(res.status, `${res.status} ${(await res.text()).slice(0, 200)}`);
  }
  const ct = res.headers.get("content-type") ?? "";
  return (ct.includes("application/json") ? res.json() : res.text()) as Promise<T>;
}

export const health = () => api<{ ok: boolean }>("/api/health");

/**
 * Resolve a (possibly relative) media path returned by the API — e.g. `manual.media.
 * originalVideoUrl` is `/api/media/:id` — into an absolute URL against the API origin so a
 * `<video src>` / frame extractor can fetch it (Range-served). Absolute URLs pass through.
 */
export function mediaUrl(path?: string | null): string | null {
  if (!path) return null;
  const abs = /^https?:\/\//i.test(path) ? path : `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;
  // <video>/<img> can't send an Authorization header, so pass the JWT as a query token
  // (the /api/media route accepts ?token=). Only applied to our own API origin.
  if (!token || !abs.startsWith(BASE)) return abs;
  return `${abs}${abs.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

/**
 * Download a media object as a Blob via authenticated fetch (Bearer header). Used for
 * client-side frame extraction: a same-origin `blob:` object URL never taints the canvas,
 * unlike a cross-origin <video src> (which fails toBlob() on iOS Safari).
 */
export async function fetchMediaBlob(path: string): Promise<Blob> {
  const url = /^https?:\/\//i.test(path) ? path : `${BASE}${path.startsWith("/") ? "" : "/"}${path}`;
  const headers = new Headers();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(url, { headers });
  if (!res.ok) throw new ApiError(res.status, `media fetch ${res.status}`);
  return res.blob();
}

// ---------- auth ----------
export interface MagicLinkResponse {
  ok: boolean;
  /** dev convenience: link/token may be echoed back when email isn't wired up */
  devToken?: string;
}
export interface VerifyResponse {
  token: string;
}

export const auth = {
  /** email + password -> JWT (primary personal login) */
  password: (email: string, password: string) =>
    api<VerifyResponse>("/api/auth/password", { method: "POST", body: JSON.stringify({ email, password }) }),
  /** change the caller's own password (currentPassword required if one is already set) */
  changePassword: (newPassword: string, currentPassword?: string) =>
    api<{ ok: boolean }>("/api/auth/password/change", {
      method: "POST",
      body: JSON.stringify({ newPassword, currentPassword }),
    }),
  /** request a magic link for an email (kept for RESEND-configured deployments) */
  magic: (email: string) =>
    api<MagicLinkResponse>("/api/auth/magic", { method: "POST", body: JSON.stringify({ email }) }),
  /** exchange a magic-link token for a JWT */
  verify: (linkToken: string) =>
    api<VerifyResponse>("/api/auth/verify", { method: "POST", body: JSON.stringify({ token: linkToken }) }),
  /** store-shared iPad: store code + PIN -> JWT */
  storePin: (storeCode: string, pin: string) =>
    api<VerifyResponse>("/api/auth/store-pin", {
      method: "POST",
      body: JSON.stringify({ storeCode, pin }),
    }),
  /**
   * Self-signup: 店舗コードで組織に合流し、常に viewer（最小権限）で開始する。
   * ロールの引き上げは管理者の「メンバー・権限」画面から。
   */
  signup: (input: { name?: string; email: string; password: string; storeCode: string }) =>
    api<VerifyResponse>("/api/auth/signup", { method: "POST", body: JSON.stringify(input) }),
  /** local dev: seed a tenant + return a usable JWT */
  devSeed: () => api<VerifyResponse>("/api/auth/dev-seed", { method: "POST" }),
};

// ---------- manuals ----------
export interface CreateManualInput {
  title?: string;
  sourceType: "image" | "video";
  storeId?: string | null;
}

/** Selection of a representative keyframe for a step (multi-candidate picker). */
export interface KeyframeSelection {
  stepId: string;
  timecodeMs: number;
}

/** Result of uploading a client-extracted keyframe JPEG for a step. */
export interface KeyframeUploadResult {
  ok: boolean;
  mediaId: string;
  imageUrl: string;
}

/**
 * Result of uploading a client-extracted *candidate* frame (POST .../candidates?t=).
 * The Worker stores the JPEG to R2 and returns a stable, servable image URL + the media id,
 * so the picker can display the R2 `<img>` directly (no blob dependency) and later adopt it
 * as the representative frame via {@link manuals.chooseKeyframe}.
 */
export interface CandidateUploadResult {
  ok: boolean;
  mediaId: string;
  imageUrl: string;
  timecodeMs: number;
}

/** Body accepted by POST .../keyframe (JSON form): adopt an existing R2 candidate as the step image. */
export interface ChooseKeyframeInput {
  mediaId: string;
  timecodeMs: number;
}

/** Per-locale translation lifecycle state. `none` = not yet translated (falls back to source). */
export type TranslationState = "none" | "queued" | "translating" | "draft" | "ready";

/** One translated locale as listed by GET /api/manuals/:id/translations. */
export interface TranslationSummary {
  locale: string;
  state: TranslationState;
  /** true once a human has reviewed/approved the machine translation */
  isHumanReviewed?: boolean;
}

/** Full translated payload (parallel to a Manual's steps) for one locale. */
export interface TranslationDetail {
  locale: string;
  state: TranslationState;
  steps: Step[];
}

/**
 * One step's editable translated fields, keyed back to the source `Step.stepId`.
 * Only the human-editable text fields travel over the wire; everything else (images,
 * timecodes, bbox) is inherited from the source step.
 */
export interface TranslatedStep {
  stepId: string;
  /** translated step title / heading */
  heading?: string;
  /** translated caption (short line under the heading) */
  caption?: string;
  /** translated long-form body, markdown allowed */
  body_markdown?: string;
}

/** Body accepted by PATCH /api/manuals/:id/translations/:locale. */
export interface SaveTranslationInput {
  steps?: TranslatedStep[];
  state?: TranslationState | string;
  isHumanReviewed?: boolean;
}

/** A single AI-generated 4-choice comprehension question. */
export interface QuizQuestion {
  question: string;
  choices: string[];
  /** index into `choices` of the correct answer */
  answerIndex: number;
  explanation: string;
  /** 1-based step order this question is derived from (when applicable) */
  stepSeq?: number;
}

/** A learner's scored attempt at a generated quiz. */
export interface QuizResult {
  /** number of correct answers */
  correct: number;
  /** total number of questions */
  total: number;
  /** rounded percentage (0..100) */
  score: number;
  /** true once score meets the pass threshold (80%) */
  passed: boolean;
}

/** A single finding from the AI quality gate. */
export interface ReviewIssue {
  severity: "high" | "medium" | "low";
  /** 1-based step order this issue refers to (omitted for manual-wide issues) */
  stepSeq?: number;
  title: string;
  suggestion?: string;
}

export interface ReviewResult {
  score: number;
  issues: ReviewIssue[];
}

export interface ApproveResult {
  ok: boolean;
  status: ManualStatus;
}

/** Result of POST /api/manuals/:id/steps/:stepId/detect — AI-detected operation target. */
export interface DetectStepResult {
  /** [y0,x0,y1,x1] normalised 0..1000, or null when nothing was detected */
  bbox: BBox | null;
  /** detected object label (e.g. "シール"), or null */
  objectLabel: string | null;
}

/** Editable fields for PATCH /api/manuals/:id/steps/:stepId. All optional (partial update). */
export interface UpdateStepInput {
  bbox?: BBox | null;
  objectLabel?: string | null;
  heading?: string;
  bodyMarkdown?: string;
  caption?: string;
  cautions?: { level: "info" | "warning" | "danger"; text: string }[];
  table?: { columns: string[]; rows: string[][] } | null;
}

export const manuals = {
  /** The API returns { manuals } (org-scoped). Filter `q` is applied client-side. */
  list: async (params?: { status?: ManualStatus; q?: string }): Promise<{ items: Manual[] }> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await api<{ manuals: Manual[] }>(`/api/manuals${suffix}`);
    return { items: res.manuals };
  },
  get: async (id: string): Promise<Manual> => {
    const res = await api<{ manual: Manual }>(`/api/manuals/${id}`);
    return res.manual;
  },
  create: async (input: CreateManualInput): Promise<Manual> => {
    const res = await api<{ manual: Manual }>("/api/manuals", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.manual;
  },
  /** Cascade-delete a manual (owner/admin/editor). */
  delete: (id: string) => api<{ ok: boolean }>(`/api/manuals/${id}`, { method: "DELETE" }),
  /**
   * Update a manual's labels / meta (owner/admin/editor). PATCH /api/manuals/:id.
   * Used by the creation-time labeling block (store / category / tags) and the Preview cover editor.
   */
  update: async (
    id: string,
    patch: { title?: string; category?: string | null; tags?: string[]; storeId?: string | null; status?: ManualStatus },
  ): Promise<Manual> => {
    const res = await api<{ manual: Manual }>(`/api/manuals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return res.manual;
  },
  /** Proxy of the ManualJob DO progress. Backend wraps it as { manualId, status, job }. */
  status: async (id: string): Promise<JobProgress> => {
    const res = await api<{ manualId: string; status: ManualStatus; job: JobProgress | null }>(
      `/api/manuals/${id}/status`,
    );
    if (res.job) return res.job;
    // No live job state yet: surface a neutral INGEST progress derived from the manual status.
    return {
      manualId: res.manualId,
      phase: res.status === "failed" ? "FAILED" : res.status === "draft" ? "INGEST" : "ASSEMBLE",
      totalSteps: 0,
      completedSteps: 0,
      updatedAt: Date.now(),
    };
  },
  /** confirm chosen keyframes for the multi-candidate picker */
  selectKeyframes: async (id: string, selections: KeyframeSelection[]): Promise<Manual> => {
    const res = await api<{ manual: Manual }>(`/api/manuals/${id}/keyframes`, {
      method: "POST",
      body: JSON.stringify({ selections }),
    });
    return res.manual;
  },
  /**
   * Upload a client-extracted keyframe (JPEG bytes) as the chosen image for one step.
   * POST /api/manuals/:id/steps/:stepId/keyframe?t=<ms>, body = raw JPEG. The Worker stores it
   * and wires it onto the step, returning the new media id + servable image URL.
   */
  uploadKeyframe: async (
    manualId: string,
    stepId: string,
    blob: Blob,
    timecodeMs: number,
  ): Promise<KeyframeUploadResult> => {
    return api<KeyframeUploadResult>(
      `/api/manuals/${manualId}/steps/${stepId}/keyframe?t=${Math.round(timecodeMs)}`,
      { method: "POST", body: blob, headers: { "content-type": blob.type || "image/jpeg" } },
    );
  },
  /**
   * Upload a client-extracted *candidate* frame (JPEG bytes) for a step. The Worker stores it
   * to R2 (best-effort, multi-candidate) and returns the new media id + a stable servable image
   * URL, so the picker shows the R2 `<img>` directly without depending on the decoded blob.
   * POST /api/manuals/:id/steps/:stepId/candidates?t=<ms>, body = raw JPEG.
   */
  uploadCandidate: async (
    manualId: string,
    stepId: string,
    blob: Blob,
    timecodeMs: number,
  ): Promise<CandidateUploadResult> => {
    return api<CandidateUploadResult>(
      `/api/manuals/${manualId}/steps/${stepId}/candidates?t=${Math.round(timecodeMs)}`,
      { method: "POST", body: blob, headers: { "content-type": blob.type || "image/jpeg" } },
    );
  },
  /**
   * Adopt an already-stored R2 candidate frame as the step's representative image.
   * POST /api/manuals/:id/steps/:stepId/keyframe with JSON body { mediaId, timecodeMs }.
   * (Distinct from {@link uploadKeyframe}, which streams raw JPEG bytes for image-flow fallback.)
   */
  chooseKeyframe: async (
    manualId: string,
    stepId: string,
    input: ChooseKeyframeInput,
  ): Promise<KeyframeUploadResult> => {
    return api<KeyframeUploadResult>(`/api/manuals/${manualId}/steps/${stepId}/keyframe`, {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    });
  },
  /** Approve + publish a manual (reviewer action from the approval queue / 提出シートのすぐ公開). */
  approve: (id: string): Promise<ApproveResult> =>
    api<ApproveResult>(`/api/manuals/${id}/approve`, { method: "POST", body: JSON.stringify({}) }),
  /** Queue machine translation into the given target locales. */
  translate: (id: string, locales: string[]): Promise<{ ok: boolean; translations: { locale: string; state: TranslationState }[] }> =>
    api<{ ok: boolean; translations: { locale: string; state: TranslationState }[] }>(
      `/api/manuals/${id}/translate`,
      { method: "POST", body: JSON.stringify({ locales }) },
    ),
  /** List per-locale translation status for a manual. */
  translations: (id: string): Promise<{ translations: TranslationSummary[] }> =>
    api<{ translations: TranslationSummary[] }>(`/api/manuals/${id}/translations`),
  /** Fetch one translated locale (state + translated steps). */
  translation: (id: string, locale: string): Promise<TranslationDetail> =>
    api<TranslationDetail>(`/api/manuals/${id}/translations/${locale}`),
  /** Persist edited translated steps / review state for one locale. */
  saveTranslation: (id: string, locale: string, body: SaveTranslationInput): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/translations/${locale}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** Run the AI quality gate; returns a score + prioritised issues. */
  review: (id: string): Promise<ReviewResult> =>
    api<ReviewResult>(`/api/manuals/${id}/review`, { method: "POST" }),
  /** Generate a comprehension quiz from a manual's steps. */
  quiz: (id: string): Promise<{ questions: QuizQuestion[] }> =>
    api<{ questions: QuizQuestion[] }>(`/api/manuals/${id}/quiz`, { method: "POST" }),
  /**
   * Detect the operation-target bbox + label for a step's chosen frame (Gemini).
   * The Worker persists the result onto the step and echoes it back. Best-effort at call sites.
   */
  detectStep: (id: string, stepId: string): Promise<DetectStepResult> =>
    api<DetectStepResult>(`/api/manuals/${id}/steps/${stepId}/detect`, { method: "POST" }),
  /** Persist edited step fields (bbox / objectLabel / text / cautions / table). Partial update. */
  updateStep: (id: string, stepId: string, body: UpdateStepInput): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/steps/${stepId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /**
   * 話して直す — send the recorded instruction audio; the Worker has Gemini apply the spoken
   * correction to the step (untouched fields preserved) and returns a 1-line 変更要約.
   */
  speakFix: (id: string, stepId: string, audio: Blob): Promise<{ ok: boolean; changeSummary: string }> =>
    api<{ ok: boolean; changeSummary: string }>(`/api/manuals/${id}/steps/${stepId}/speak-fix`, {
      method: "POST",
      body: audio,
      headers: { "content-type": audio.type || "audio/webm" },
    }),
  /**
   * Persist a step's highlighter marks (freehand strokes + body key phrases). Sent as a
   * partial PATCH on the step (only the `highlights` field travels). The backend normalises
   * + clamps coordinates to the 0..1000 space.
   */
  saveHighlights: (id: string, stepId: string, highlights: StepHighlights): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/steps/${stepId}`, {
      method: "PATCH",
      body: JSON.stringify({ highlights }),
    }),
  /**
   * Ask the AI to auto-mark a step: it extracts key body phrases (terms) and, when a bbox
   * exists, draws a marker stroke across it. The result is merged with existing marks and
   * persisted server-side; the merged {@link StepHighlights} is echoed back.
   */
  autoHighlight: (id: string, stepId: string): Promise<{ highlights: StepHighlights }> =>
    api<{ highlights: StepHighlights }>(`/api/manuals/${id}/steps/${stepId}/highlight`, {
      method: "POST",
    }),

  /**
   * Persist the CALLER's personal highlights for a step (viewer-role inclusive).
   * PUT /api/manuals/:id/steps/:stepId/my-highlights
   */
  saveMyHighlights: (id: string, stepId: string, highlights: StepHighlights): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/steps/${stepId}/my-highlights`, {
      method: "PUT",
      body: JSON.stringify(highlights),
    }),

  /**
   * Fetch the auto-generated checklist + the caller's progress against it.
   * GET /api/manuals/:id/checklist — returns { checklist, progress }
   */
  getChecklist: (id: string): Promise<{ checklist: ManualChecklist; progress: ChecklistProgress }> =>
    api<{ checklist: ManualChecklist; progress: ChecklistProgress }>(`/api/manuals/${id}/checklist`),

  /**
   * Regenerate the checklist from the manual's current highlights.
   * POST /api/manuals/:id/checklist/regenerate — returns same shape as getChecklist.
   */
  regenerateChecklist: (id: string): Promise<{ checklist: ManualChecklist; progress: ChecklistProgress }> =>
    api<{ checklist: ManualChecklist; progress: ChecklistProgress }>(
      `/api/manuals/${id}/checklist/regenerate`,
      { method: "POST" },
    ),

  /**
   * Persist the caller's checklist progress (which items have been ticked off).
   * PUT /api/manuals/:id/checklist/progress — body { doneItemIds: string[] }
   */
  saveChecklistProgress: (id: string, doneItemIds: string[]): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/checklist/progress`, {
      method: "PUT",
      body: JSON.stringify({ doneItemIds }),
    }),

  /**
   * Insert a blank step into the manual.
   * POST /api/manuals/:id/steps — body { afterStepId: string | null }
   *  - null  → insert before the first step
   *  - string → insert directly after that step
   */
  insertStep: (id: string, afterStepId: string | null): Promise<{ ok: true; stepId: string }> =>
    api<{ ok: true; stepId: string }>(`/api/manuals/${id}/steps`, {
      method: "POST",
      body: JSON.stringify({ afterStepId }),
    }),

  /**
   * Delete a step from the manual. The last step cannot be deleted (400).
   * DELETE /api/manuals/:id/steps/:stepId
   */
  deleteStep: (id: string, stepId: string): Promise<{ ok: true }> =>
    api<{ ok: true }>(`/api/manuals/${id}/steps/${stepId}`, { method: "DELETE" }),
};

// ---------- uploads (Worker-proxied PUT; matches POST /init -> PUT /:mediaId -> POST /:mediaId/complete) ----------
export type MediaKind = "video" | "audio" | "image" | "frame" | "pdf";

/** One file to register with the upload session. */
export interface UploadFileSpec {
  kind: MediaKind;
  mimeType: string;
}

/** A PUT target returned by /init: stream the bytes to `uploadUrl` (relative to the API origin). */
export interface UploadTarget {
  mediaId: string;
  kind: MediaKind;
  mimeType: string;
  /** PUT here (path relative to the API origin) */
  uploadUrl: string;
  r2Key: string;
}

export interface UploadInit {
  manualId: string;
  method: string;
  uploads: UploadTarget[];
  /**
   * Legacy convenience for the offline queue: a presigned-style view derived from `uploads`,
   * so older callers can iterate `parts[i].url` (here the Worker-proxied PUT path).
   */
  parts: { partNumber: number; url: string }[];
  /** Legacy alias; the first media id drives the queue message. Empty when no uploads. */
  uploadId: string;
}

export interface UploadCompleteResult {
  ok: boolean;
  manualId: string;
  jobId: string;
  status: ManualStatus;
}

/** New (backend-aligned) init input. */
interface UploadInitInput {
  sourceType: "image" | "video";
  files: UploadFileSpec[];
  manualId?: string;
  title?: string;
  storeId?: string | null;
}
/** Legacy init input used by the offline queue (single file described by content-type). */
interface UploadInitLegacyInput {
  manualId?: string;
  filename: string;
  contentType: string;
  size: number;
  sourceType?: "image" | "video";
}

function kindForMime(mime: string): MediaKind {
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf") return "pdf";
  return "image";
}

async function initUpload(input: UploadInitInput | UploadInitLegacyInput): Promise<UploadInit> {
  // Normalise both the new and legacy shapes into the backend's /init contract.
  const files: UploadFileSpec[] =
    "files" in input
      ? input.files
      : [{ kind: kindForMime(input.contentType), mimeType: input.contentType }];
  const sourceType: "image" | "video" =
    input.sourceType ?? (files[0]?.kind === "video" ? "video" : "image");
  const body = {
    sourceType,
    files,
    manualId: input.manualId,
    title: "title" in input ? input.title : undefined,
    storeId: "storeId" in input ? input.storeId : undefined,
  };
  const res = await api<{ manualId: string; method: string; uploads: UploadTarget[] }>(
    "/api/uploads/init",
    { method: "POST", body: JSON.stringify(body) },
  );
  return {
    ...res,
    parts: res.uploads.map((u, i) => ({ partNumber: i + 1, url: u.uploadUrl })),
    uploadId: res.uploads[0]?.mediaId ?? "",
  };
}

/** Chunk size for multipart uploads — uniform (R2 requires same-size parts except the last). */
const MPU_PART_BYTES = 16 * 1024 * 1024;
/** Above this size, uploads switch to chunked multipart (edge cap is ~100MB per request). */
const MPU_THRESHOLD_BYTES = 60 * 1024 * 1024;

/** Attempts per upload request (init / mpu create / each part / complete) before giving up. */
const UPLOAD_MAX_ATTEMPTS = 4;
/** Backoff before attempt n+1 (ms). */
const UPLOAD_RETRY_DELAYS_MS = [1_000, 3_000, 7_000];

/** Transient = worth retrying: network drop (fetch TypeError), timeout, rate limit, or 5xx. */
export function isTransientUploadError(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 408 || e.status === 429 || e.status >= 500;
  return e instanceof TypeError;
}

/**
 * Retry an upload request on transient failures. A long recording is 15+ sequential requests, so
 * without this a single dropped request on a mobile network killed the whole upload. Every request
 * in the handshake is safe to repeat: init/create return fresh ids, a re-PUT part replaces the same
 * part number in R2, and complete is rejected (not duplicated) once the upload is already done.
 */
export async function withUploadRetry<T>(fn: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= UPLOAD_MAX_ATTEMPTS || !isTransientUploadError(e)) throw e;
      await new Promise((r) => setTimeout(r, UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 7_000));
    }
  }
}

/** Chunked upload: mpu create → uniform PUT parts → complete. Aborts server-side on failure. */
async function putMultipart(mediaId: string, blob: Blob, onProgress?: (frac: number) => void): Promise<string | null> {
  const create = await withUploadRetry(() =>
    api<{ uploadId: string }>(`/api/uploads/${mediaId}/mpu`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  );
  const uploadId = create.uploadId;
  const totalParts = Math.ceil(blob.size / MPU_PART_BYTES);
  const parts: { partNumber: number; etag: string }[] = [];
  try {
    for (let i = 0; i < totalParts; i++) {
      const chunk = blob.slice(i * MPU_PART_BYTES, Math.min((i + 1) * MPU_PART_BYTES, blob.size));
      // Re-uploading the same part number replaces it in R2, so a retried part is idempotent.
      const part = await withUploadRetry(async () => {
        const headers = new Headers({ "content-type": "application/octet-stream" });
        if (token) headers.set("authorization", `Bearer ${token}`);
        const res = await fetch(`${BASE}/api/uploads/${mediaId}/mpu/${uploadId}/${i + 1}`, {
          method: "PUT",
          body: chunk,
          headers,
        });
        if (!res.ok) throw new ApiError(res.status, `upload part ${i + 1}/${totalParts} failed ${res.status}`);
        return (await res.json()) as { partNumber: number; etag: string };
      });
      parts.push(part);
      onProgress?.((i + 1) / totalParts);
    }
    // The last request of a minutes-long transfer is the one most exposed to network drift —
    // losing it here would discard every part already uploaded.
    const done = await withUploadRetry(() =>
      api<{ ok: boolean; r2Key?: string }>(`/api/uploads/${mediaId}/mpu/${uploadId}/complete`, {
        method: "POST",
        body: JSON.stringify({ parts, sizeBytes: blob.size }),
      }),
    );
    return done.r2Key ?? null;
  } catch (e) {
    // Best-effort abort so orphaned parts don't linger in R2; the caller sees the original error.
    void api(`/api/uploads/${mediaId}/mpu/${uploadId}`, { method: "DELETE" }).catch(() => {});
    throw e;
  }
}

export const uploads = {
  /**
   * Create (or reuse) a draft manual + media rows and get PUT targets back.
   * `sourceType` drives the pipeline; `files` lists every media object (e.g. image + audio pairs).
   * Accepts the legacy `{ filename, contentType, size }` shape for the offline queue.
   */
  init: (input: UploadInitInput | UploadInitLegacyInput): Promise<UploadInit> => initUpload(input),
  /**
   * Stream raw bytes for a media object: PUT /api/uploads/:mediaId (Worker proxies into R2).
   * `target` is the media id; the legacy presigned `url` form (parts[i].url) also resolves
   * because the backend PUT path is `/api/uploads/:mediaId`. Returns the stored r2Key (or null).
   *
   * Large blobs are sent as CHUNKS via R2 multipart — the Cloudflare edge rejects request
   * bodies around 100MB, so a long recording (e.g. a 139MB screen capture) can never arrive
   * as one PUT. `onProgress` reports 0..1 across the whole transfer (part-level granularity).
   */
  put: async (target: string, blob: Blob, onProgress?: (frac: number) => void): Promise<string | null> => {
    // Accept either a bare media id or the path returned in parts[].url (/api/uploads/:id).
    const mediaId = target.startsWith("/") ? target.split("/").pop() ?? target : target;
    if (blob.size > MPU_THRESHOLD_BYTES) return putMultipart(mediaId, blob, onProgress);
    // Single PUT overwrites the same R2 key, so a retried PUT is idempotent too.
    const out = await withUploadRetry(async () => {
      const headers = new Headers();
      if (blob.type) headers.set("content-type", blob.type);
      if (token) headers.set("authorization", `Bearer ${token}`);
      const res = await fetch(`${BASE}/api/uploads/${mediaId}`, { method: "PUT", body: blob, headers });
      if (!res.ok) throw new ApiError(res.status, `upload PUT failed ${res.status}`);
      return (await res.json().catch(() => null)) as { r2Key?: string } | null;
    });
    onProgress?.(1);
    return out?.r2Key ?? null;
  },
  /**
   * Mark a media object uploaded; the backend creates the job + enqueues processing.
   * New form: complete(mediaId, { manualId, primaryMediaId? }).
   * Legacy form (offline queue): complete({ uploadId, manualId, etags? }).
   */
  complete: (
    a: string | { uploadId: string; manualId: string; etags?: (string | null)[] },
    b?: { manualId: string; primaryMediaId?: string },
  ): Promise<UploadCompleteResult> => {
    const mediaId = typeof a === "string" ? a : a.uploadId;
    const input = typeof a === "string" ? b! : { manualId: a.manualId };
    return api<UploadCompleteResult>(`/api/uploads/${mediaId}/complete`, {
      method: "POST",
      body: JSON.stringify({ manualId: input.manualId, primaryMediaId: input.primaryMediaId }),
    });
  },
};

// ---------- stores (org-scoped; admin CRUD) ----------
export const stores = {
  /** GET /api/stores → { stores }. Org-scoped list of stores (店舗). */
  list: async (): Promise<Store[]> => {
    const res = await api<{ stores: Store[] }>("/api/stores");
    return res.stores;
  },
  /** GET /api/stores/:id → { store }. */
  get: async (id: string): Promise<Store> => {
    const res = await api<{ store: Store }>(`/api/stores/${id}`);
    return res.store;
  },
  /** POST /api/stores (admin). */
  create: async (input: { name: string; divisionId?: string | null }): Promise<Store> =>
    (await api<{ store: Store }>("/api/stores", { method: "POST", body: JSON.stringify(input) })).store,
  /** PATCH /api/stores/:id (admin) — rename / move between 事業室 / set the 店舗コード. */
  update: async (id: string, patch: { name?: string; divisionId?: string | null; code?: string | null }): Promise<Store> =>
    (await api<{ store: Store }>(`/api/stores/${id}`, { method: "PATCH", body: JSON.stringify(patch) })).store,
  /** DELETE /api/stores/:id (admin). */
  remove: (id: string) => api<{ ok: boolean }>(`/api/stores/${id}`, { method: "DELETE" }),
  /** POST /api/stores/seed-defaults (admin) — idempotent default 店舗+事業室 set. */
  seedDefaults: () =>
    api<{ ok: boolean; createdStores: number; createdDivisions: number }>("/api/stores/seed-defaults", {
      method: "POST",
      body: JSON.stringify({}),
    }),
};

// ---------- 事業室 (divisions: store groups) ----------

export const divisions = {
  /** GET /api/divisions → org's 事業室 (position order). */
  list: async (): Promise<Division[]> => (await api<{ divisions: Division[] }>("/api/divisions")).divisions,
  create: async (name: string): Promise<Division> =>
    (await api<{ division: Division }>("/api/divisions", { method: "POST", body: JSON.stringify({ name }) })).division,
  update: async (id: string, name: string): Promise<Division> =>
    (await api<{ division: Division }>(`/api/divisions/${id}`, { method: "PATCH", body: JSON.stringify({ name }) })).division,
  /** Delete — its stores fall back to 未所属. */
  remove: (id: string) => api<{ ok: boolean }>(`/api/divisions/${id}`, { method: "DELETE" }),
};

// ---------- 本 (books: curated, ordered manual collections with a target audience) ----------

export interface BookInput {
  title: string;
  description?: string;
  audience?: Partial<BookAudience>;
  mustRead?: boolean;
  /** optional initial contents (ordered) */
  manualIds?: string[];
}

export const books = {
  /** GET /api/books → all org books, each with its ordered manualIds. */
  list: async (): Promise<Book[]> => (await api<{ books: Book[] }>("/api/books")).books,
  get: async (id: string): Promise<Book> => (await api<{ book: Book }>(`/api/books/${id}`)).book,
  create: async (input: BookInput): Promise<Book> =>
    (await api<{ book: Book }>("/api/books", { method: "POST", body: JSON.stringify(input) })).book,
  update: async (
    id: string,
    patch: { title?: string; description?: string | null; audience?: Partial<BookAudience>; mustRead?: boolean },
  ): Promise<Book> =>
    (await api<{ book: Book }>(`/api/books/${id}`, { method: "PATCH", body: JSON.stringify(patch) })).book,
  remove: (id: string) => api<{ ok: boolean }>(`/api/books/${id}`, { method: "DELETE" }),
  /** Replace the ordered contents (array order = reading order). */
  setItems: async (id: string, manualIds: string[]): Promise<string[]> =>
    (await api<{ manualIds: string[] }>(`/api/books/${id}/items`, { method: "PUT", body: JSON.stringify({ manualIds }) })).manualIds,
  /** Append one manual (the tag-like "この本に入れる" gesture). */
  addItem: async (id: string, manualId: string): Promise<Book> =>
    (await api<{ book: Book }>(`/api/books/${id}/items`, { method: "POST", body: JSON.stringify({ manualId }) })).book,
  removeItem: async (id: string, manualId: string): Promise<Book> =>
    (await api<{ book: Book }>(`/api/books/${id}/items/${manualId}`, { method: "DELETE" })).book,
};

/** True when a book targets the given viewer (claims stores/roles). Empty audience = everyone. */
export function bookTargetsViewer(book: Book, claims: Claims | null): boolean {
  if (!claims) return false;
  const storeOk =
    book.audience.storeIds.length === 0 ||
    claims.stores.length === 0 || // org-wide membership sees every store's books
    book.audience.storeIds.some((s) => claims.stores.includes(s));
  const roleOk = book.audience.roles.length === 0 || claims.roles.some((r) => book.audience.roles.includes(r));
  return storeOk && roleOk;
}

// ---------- reading progress (server mirror of the on-device cache) ----------

export const progress = {
  /** PUT /api/progress/:manualId — report the caller's own progress (best-effort mirror).
   *  `keepalive` lets the final flush survive tab close / pagehide. */
  put: (
    manualId: string,
    entry: { readPct: number; lastStep: number; quizScore?: number | null; quizPassed?: boolean },
    opts?: { keepalive?: boolean },
  ) =>
    api<{ ok: boolean }>(`/api/progress/${manualId}`, {
      method: "PUT",
      body: JSON.stringify(entry),
      keepalive: opts?.keepalive,
    }),
  /** GET /api/progress/me → the caller's entries. */
  me: async (): Promise<ReadingProgressEntry[]> => (await api<{ entries: ReadingProgressEntry[] }>("/api/progress/me")).entries,
  /** GET /api/progress/summary → org-wide rows for 学習管理 (owner/admin/reviewer). */
  summary: async (manualIds?: string[]): Promise<ReadingProgressSummaryRow[]> => {
    const qs = manualIds?.length ? `?manualIds=${encodeURIComponent(manualIds.join(","))}` : "";
    return (await api<{ rows: ReadingProgressSummaryRow[] }>(`/api/progress/summary${qs}`)).rows;
  },
};

// ---------- members (user + membership) ----------
/** Body accepted by POST /api/members (admin invite). */
export interface InviteMemberInput {
  email: string;
  displayName?: string;
  role: Role;
  /** store scope; null = whole-org membership */
  storeId?: string | null;
}

/**
 * A membership joined with its user + (optional) store, ready for display. The list endpoint
 * (`GET /api/members`) only returns the bare {@link Membership} rows (no name/email), so
 * {@link members.listDetailed} enriches each one via `GET /api/members/:id` (→ the user) and
 * resolves the store name from {@link stores.list}. `user`/`store` may be null when a lookup
 * fails so a single bad row never blanks the whole table.
 */
export interface MemberDetail {
  membership: Membership;
  user: User | null;
  /** resolved store for `membership.storeId`; null = whole-org membership (or unknown id) */
  store: Store | null;
}

export const members = {
  /** GET /api/members → { memberships }. Bare membership rows (no user name/email). */
  list: async (): Promise<Membership[]> => {
    const res = await api<{ memberships: Membership[] }>("/api/members");
    return res.memberships;
  },
  /** GET /api/members/:id → { user }. Resolves a member's user record by id. */
  get: async (userId: string): Promise<User> => {
    const res = await api<{ user: User }>(`/api/members/${userId}`);
    return res.user;
  },
  /** POST /api/members (admin) → { user, membership }. Invite a member with a role + store scope. */
  invite: async (input: InviteMemberInput): Promise<{ user: User; membership: Membership }> =>
    api<{ user: User; membership: Membership }>("/api/members", {
      method: "POST",
      body: JSON.stringify({ ...input, storeId: input.storeId ?? null }),
    }),
  /** PATCH /api/members/:id (admin) → rename a member. */
  updateProfile: (userId: string, input: { displayName: string }): Promise<{ user: User }> =>
    api<{ user: User }>(`/api/members/${userId}`, { method: "PATCH", body: JSON.stringify(input) }),
  /**
   * PATCH /api/members/:id/memberships/:membershipId (admin) → change one membership's role.
   * The org's last owner cannot be demoted (400 cannot_demote_last_owner).
   */
  updateRole: (userId: string, membershipId: string, role: Role): Promise<{ membership: Membership }> =>
    api<{ membership: Membership }>(`/api/members/${userId}/memberships/${membershipId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  /** POST /api/members/:id/password (admin) → set/reset a member's login password. */
  setPassword: (userId: string, password: string) =>
    api<{ ok: boolean }>(`/api/members/${userId}/password`, { method: "POST", body: JSON.stringify({ password }) }),
  /**
   * Display-ready member list: fetches memberships + stores, then resolves each membership's user.
   * User/store lookups are best-effort per row (a failed user fetch yields `user: null` rather
   * than failing the whole list). Distinct users are fetched once even across multiple memberships.
   */
  listDetailed: async (): Promise<MemberDetail[]> => {
    const [memberships, storeList] = await Promise.all([members.list(), stores.list()]);
    const storeById = new Map(storeList.map((s) => [s.id, s]));
    const uniqueUserIds = [...new Set(memberships.map((m) => m.userId))];
    const userEntries = await Promise.all(
      uniqueUserIds.map(async (id): Promise<[string, User | null]> => {
        try {
          return [id, await members.get(id)];
        } catch {
          return [id, null];
        }
      }),
    );
    const userById = new Map(userEntries);
    return memberships.map((membership) => ({
      membership,
      user: userById.get(membership.userId) ?? null,
      store: membership.storeId ? storeById.get(membership.storeId) ?? null : null,
    }));
  },
};
